<template>
  <div v-if="visible" class="waajo-ticket-modal-backdrop" @click.self="handleBackdropClick">
    <div class="waajo-ticket-modal" role="dialog" aria-modal="true" aria-labelledby="waajoTicketModalTitle">
      <div class="waajo-ticket-modal-header">
        <div>
          <h4 id="waajoTicketModalTitle"><i class="fa fa-ticket"></i> Buat Tiket</h4>
          <p>Catat keluhan atau permintaan pelanggan. Tiket tidak mengirim pesan WhatsApp otomatis.</p>
        </div>
        <button type="button" class="waajo-ticket-modal-close" :disabled="submitting" @click="close">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <form class="waajo-ticket-modal-body" @submit.prevent="submit">
        <div v-if="!isManual" class="waajo-ticket-context">
          <div>
            <span>Pelanggan</span>
            <strong>{{ context.contact_name || context.contact_number || '-' }}</strong>
            <small>{{ context.contact_number || '-' }}</small>
          </div>
          <div>
            <span>Nomor WhatsApp CS</span>
            <strong>{{ contextLicenseLabel }}</strong>
            <small>Room: {{ context.room_id || '-' }}</small>
          </div>
        </div>

        <div v-if="isManual" class="waajo-ticket-form-group">
          <label>Pelanggan <em>*</em></label>
          <div class="waajo-ticket-contact-box">
            <ticket-contact-selector
              ref="contactSelector"
              :disabled="locked"
              @add-contact="showContactModal = true"
              @selected="handleContactSelected"
            />
          </div>
          <small class="waajo-ticket-field-help">
            Pilih kontak existing atau tambahkan kontak baru. Nomor pelanggan diambil dari master Kontak.
          </small>
        </div>

        <div v-if="isManual" class="waajo-ticket-form-group">
          <label>Nomor WhatsApp CS <em>*</em></label>
          <select v-model="form.license_id" :disabled="locked || loadingLicenses" @change="onLicenseChange">
            <option value="">Pilih nomor WhatsApp CS</option>
            <option v-for="license in licenses" :key="license.id" :value="license.id">
              {{ licenseOptionLabel(license) }}
            </option>
          </select>
          <small class="waajo-ticket-field-help">Pilih nomor WhatsApp CS yang digunakan untuk menerima pesan pelanggan.</small>
          <small v-if="loadingLicenses" class="waajo-ticket-field-state">
            <i class="fa fa-spinner fa-spin"></i> Memuat nomor WhatsApp CS...
          </small>
          <small v-else-if="licenseLoadError" class="waajo-ticket-field-state is-error">
            <i class="fa fa-exclamation-circle"></i> {{ licenseLoadError }}
            <button type="button" class="waajo-ticket-retry-btn" @click="loadLicenses">Coba lagi</button>
          </small>
          <small v-else-if="!licenses.length" class="waajo-ticket-field-state is-empty">
            Tidak ada nomor WhatsApp CS aktif. Pastikan lisensi WhatsApp sudah aktif.
          </small>
        </div>

        <div class="waajo-ticket-form-group">
          <label>Judul Tiket <em>*</em></label>
          <input v-model.trim="form.title" type="text" maxlength="255" placeholder="Ringkasan singkat masalah pelanggan" :disabled="locked">
        </div>

        <div class="waajo-ticket-form-grid">
          <div class="waajo-ticket-form-group">
            <label>Kategori <em>*</em></label>
            <select v-model="form.category" :disabled="locked">
              <option value="">Pilih kategori</option>
              <option v-for="item in categories" :key="item.value" :value="item.value">{{ item.label }}</option>
            </select>
          </div>
          <div class="waajo-ticket-form-group">
            <label>Prioritas <em>*</em></label>
            <select v-model="form.priority" :disabled="locked">
              <option v-for="item in priorities" :key="item.value" :value="item.value">{{ item.label }}</option>
            </select>
          </div>
        </div>

        <div class="waajo-ticket-form-group">
          <div class="waajo-ticket-label-row">
            <label>Deskripsi <em>*</em></label>
            <button v-if="hasLastMessage" type="button" class="waajo-ticket-link-btn" :disabled="locked" @click="useLastMessage">
              Gunakan pesan terakhir
            </button>
          </div>
          <div v-if="hasLastMessage" class="waajo-ticket-last-message">
            <i class="fa fa-quote-left"></i>
            <span>{{ lastMessagePreview }}</span>
          </div>
          <textarea v-model.trim="form.description" rows="5" maxlength="10000" placeholder="Jelaskan keluhan atau permintaan pelanggan" :disabled="locked"></textarea>
          <small class="waajo-ticket-helper">Deskripsi tidak otomatis dikirim ke pelanggan.</small>
        </div>

        <div class="waajo-ticket-form-grid">
          <div class="waajo-ticket-form-group">
            <label>PIC / Agent</label>
            <select v-model="form.assigned_agent_id" :disabled="locked || loadingAgents || !form.license_id">
              <option value="">Belum ditentukan</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}<template v-if="agent.active_ticket_count"> — {{ agent.active_ticket_count }} tiket aktif</template>
              </option>
            </select>
            <small v-if="loadingAgents"><i class="fa fa-spinner fa-spin"></i> Memuat Agent...</small>
            <small v-else-if="form.license_id && !agents.length">Tidak ada Agent aktif pada lisensi ini.</small>
          </div>
          <div class="waajo-ticket-form-group">
            <label>Jadwal Tindak Lanjut</label>
            <input v-model="form.follow_up_local" type="datetime-local" :disabled="locked">
          </div>
        </div>

        <div class="waajo-ticket-form-group">
          <ticket-attachment-picker
            :files="attachmentFiles"
            :disabled="submitting || attachmentUploading"
            :busy="attachmentUploading"
            :progress="attachmentProgress"
            @change="handleAttachmentFilesChange"
          />
        </div>

        <div v-if="createdTicket" class="waajo-ticket-created-notice" :class="{ 'is-warning': !!attachmentUploadError }">
          <i :class="attachmentUploadError ? 'fa fa-exclamation-triangle' : 'fa fa-check-circle'"></i>
          <div class="waajo-ticket-created-notice-body">
            <strong>
              Tiket {{ createdTicket.ticket_number || createdTicket.id }} berhasil dibuat.
            </strong>
            <p v-if="attachmentUploadError">
              Gambar belum berhasil diunggah: {{ attachmentUploadError }}
            </p>
            <p v-else-if="attachmentUploading">
              Mengunggah {{ attachmentFiles.length }} gambar... {{ attachmentProgress }}%
            </p>
            <p v-else-if="!attachmentFiles.length">
              Tiket sudah tersimpan. Anda dapat menambahkan gambar dari halaman
              detail tiket.
            </p>
            <div v-if="attachmentUploadError" class="waajo-ticket-created-notice-actions">
              <button
                type="button"
                class="waajo-ticket-btn is-primary"
                :disabled="attachmentUploading || !attachmentFiles.length"
                @click="retryAttachmentUpload"
              >
                <i class="fa fa-refresh"></i> Coba Unggah Lagi
              </button>
              <button
                type="button"
                class="waajo-ticket-btn"
                :disabled="attachmentUploading"
                @click="openCreatedTicketDetail"
              >
                <i class="fa fa-external-link"></i> Buka Detail Tiket
              </button>
            </div>
          </div>
        </div>

        <div v-if="apiError" class="waajo-ticket-form-error">
          <i class="fa fa-exclamation-circle"></i> {{ apiError }}
        </div>

        <div class="waajo-ticket-modal-footer">
          <template v-if="!createdTicket">
            <button type="button" class="waajo-ticket-btn" :disabled="locked" @click="close">Batal</button>
            <button type="submit" class="waajo-ticket-btn is-primary" :disabled="locked || loadingAgents">
              <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
              {{ submitting ? 'Menyimpan...' : 'Simpan Tiket' }}
            </button>
          </template>
          <template v-else>
            <button type="button" class="waajo-ticket-btn" :disabled="attachmentUploading" @click="openCreatedTicketDetail">
              Buka Detail Tiket
            </button>
            <button type="button" class="waajo-ticket-btn is-primary" :disabled="attachmentUploading" @click="close">
              Tutup
            </button>
          </template>
        </div>
      </form>
    </div>

    <contact-form-modal-v2
      :visible="showContactModal"
      mode="create"
      :overlay-z-index="2300"
      @close="showContactModal = false"
      @saved="handleContactCreated"
    />
  </div>
</template>

<script>
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  datetimeLocalToUnix,
  extractApiErrorMessage,
  formatTicketPhone,
  ticketContactNumber
} from '@/utils/ticket'
import TicketContactSelector from './TicketContactSelector.vue'
import TicketAttachmentPicker from './TicketAttachmentPicker.vue'
import ContactFormModalV2 from '@/views/contacts/ContactFormModalV2.vue'

const emptyForm = () => ({
  contact_id: '',
  license_id: '',
  contact_number: '',
  contact_name: '',
  title: '',
  description: '',
  category: '',
  priority: 'normal',
  assigned_agent_id: '',
  follow_up_local: ''
})

export default {
  name: 'TicketCreateModal',
  components: {
    TicketContactSelector,
    TicketAttachmentPicker,
    ContactFormModalV2
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    context: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      categories: TICKET_CATEGORIES,
      priorities: TICKET_PRIORITIES,
      form: emptyForm(),
      licenses: [],
      agents: [],
      loadingLicenses: false,
      loadingAgents: false,
      licensesRequestId: 0,
      agentsRequestId: 0,
      submitting: false,
      licenseLoadError: '',
      apiError: '',
      showContactModal: false,
      attachmentFiles: [],
      attachmentUploading: false,
      attachmentProgress: 0,
      attachmentUploadError: '',
      createdTicket: null
    }
  },
  computed: {
    locked() {
      return this.submitting || !!this.createdTicket
    },
    isManual() {
      return !this.context || !this.context.license_id
    },
    hasLastMessage() {
      return !!(this.context && this.context.last_message && String(this.context.last_message).trim())
    },
    lastMessagePreview() {
      const value = this.hasLastMessage ? String(this.context.last_message).trim() : ''
      return value.length > 220 ? value.slice(0, 220) + '...' : value
    },
    contextLicenseLabel() {
      if (!this.context) return '-'
      const phone = formatTicketPhone(this.context.license_phone_number, '')
      const name = String(this.context.license_name || '').trim()
      if (name && phone) return name + ' — ' + phone
      return phone || name || this.context.license_id || '-'
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.initialize()
      } else {
        // Antrean file dilepas saat modal ditutup; picker juga me-revoke
        // object URL pada beforeDestroy.
        this.attachmentFiles = []
        this.attachmentUploadError = ''
        this.attachmentProgress = 0
      }
    }
  },
  methods: {
    initialize() {
      this.apiError = ''
      this.licenseLoadError = ''
      this.submitting = false
      this.attachmentFiles = []
      this.attachmentUploading = false
      this.attachmentProgress = 0
      this.attachmentUploadError = ''
      this.createdTicket = null
      this.licensesRequestId += 1
      this.agentsRequestId += 1
      this.loadingLicenses = false
      this.loadingAgents = false
      this.agents = []
      this.licenses = []
      const context = this.context || {}
      this.form = Object.assign(emptyForm(), {
        contact_id: String(context.contact_id || '').trim(),
        license_id: String(context.license_id || '').trim(),
        contact_number: String(context.contact_number || '').trim(),
        contact_name: String(context.contact_name || '').trim(),
        assigned_agent_id: String(context.default_agent_id || '').trim()
      })
      this.showContactModal = false
      if (this.isManual) {
        this.loadLicenses()
        this.$nextTick(() => {
          if (this.$refs.contactSelector) {
            this.$refs.contactSelector.clearSelection()
          }
        })
      } else {
        this.loadAgents(context.license_id)
      }
    },
    licenseOptionLabel(license) {
      if (!license) return ''
      const phone = formatTicketPhone(license.license_phone_number, '')
      const name = String(license.license_name || '').trim()
      let label = name && phone
        ? name + ' — ' + phone
        : (phone || name || String(license.id || ''))
      const online = Number(license.is_online_status)
      if (online === 1) {
        label += ' · Online'
      } else if (online === 0) {
        label += ' · Offline'
      }
      return label
    },
    async loadLicenses() {
      const requestId = this.licensesRequestId + 1
      this.licensesRequestId = requestId
      this.loadingLicenses = true
      this.licenseLoadError = ''
      this.apiError = ''
      try {
        const response = await this.$store.dispatch('license/getLicenseList', {
          license_name: '',
          license_phone_number: '',
          license_status: 'active',
          order_by: 'latest',
          page: 0,
          perPage: 100
        })
        if (requestId !== this.licensesRequestId) return
        const payload = response && response.data ? response.data : {}
        const rows = Array.isArray(payload.data)
          ? payload.data
          : (Array.isArray(payload) ? payload : [])
        this.licenses = rows.filter(item => {
          const label = String(item.status_label || '').toLowerCase()
          return !label || label === 'active'
        })
        if (
          this.form.license_id &&
          !this.licenses.some(item => String(item.id) === String(this.form.license_id))
        ) {
          this.form.license_id = ''
          this.form.assigned_agent_id = ''
        }
      } catch (error) {
        if (requestId !== this.licensesRequestId) return
        this.licenses = []
        this.licenseLoadError = extractApiErrorMessage(
          error,
          'Daftar nomor WhatsApp CS gagal dimuat.'
        )
      } finally {
        if (requestId === this.licensesRequestId) {
          this.loadingLicenses = false
        }
      }
    },
    async loadAgents(licenseId) {
      const requestId = this.agentsRequestId + 1
      this.agentsRequestId = requestId
      this.agents = []
      if (!licenseId) {
        this.loadingAgents = false
        return
      }
      this.loadingAgents = true
      this.apiError = ''
      try {
        const response = await this.$store.dispatch('ticket/agents', { license_id: licenseId })
        if (requestId !== this.agentsRequestId) return
        this.agents = Array.isArray(response.data) ? response.data : []
        if (this.form.assigned_agent_id) {
          const found = this.agents.find(agent => agent.id === this.form.assigned_agent_id)
          if (!found) this.form.assigned_agent_id = ''
        }
      } catch (error) {
        if (requestId !== this.agentsRequestId) return
        this.agents = []
        this.apiError = extractApiErrorMessage(error, 'Gagal memuat Agent')
      } finally {
        if (requestId === this.agentsRequestId) {
          this.loadingAgents = false
        }
      }
    },
    onLicenseChange() {
      this.form.assigned_agent_id = ''
      this.loadAgents(this.form.license_id)
    },
    handleContactSelected(contact) {
      if (!contact) {
        this.form.contact_id = ''
        this.form.contact_number = ''
        this.form.contact_name = ''
        return
      }
      this.form.contact_id = String(contact.id || contact.Id || '').trim()
      this.form.contact_number = ticketContactNumber(contact)
      this.form.contact_name = this.contactDisplayName(contact)
    },
    contactDisplayName(contact) {
      if (!contact) return ''
      return String(contact.name || ((contact.first_name || '') + ' ' + (contact.last_name || '')).trim() || '').trim()
    },
    handleContactCreated(payload) {
      this.showContactModal = false
      const contact = payload && payload.contact
        ? payload.contact
        : (payload && (payload.phone_number || payload.id || payload.Id) ? payload : null)
      if (payload && payload.warning) {
        this.$fire({
          title: 'Perhatian',
          text: payload.warning,
          type: 'warning',
          timer: 3500
        })
      }
      if (!contact) return
      const selected = {
        id: String(contact.id || contact.Id || '').trim(),
        first_name: String(contact.first_name || '').trim(),
        last_name: String(contact.last_name || '').trim(),
        phone_number: ticketContactNumber(contact),
        email: String(contact.email || '').trim()
      }
      if (!selected.phone_number) return
      if (this.$refs.contactSelector) {
        this.$refs.contactSelector.selectContact(selected)
      } else {
        this.handleContactSelected(selected)
      }
    },
    useLastMessage() {
      if (!this.hasLastMessage) return
      const reference = 'Pesan terakhir pelanggan: "' + String(this.context.last_message).trim() + '"'
      const nextValue = this.form.description
        ? this.form.description + '\n\n' + reference
        : reference
      this.form.description = nextValue.slice(0, 10000)
    },
    validate() {
      if (!this.form.license_id) return 'Nomor WhatsApp CS wajib dipilih.'
      if (!this.form.contact_number) return 'Pilih kontak pelanggan terlebih dahulu.'
      if (!this.form.title) return 'Judul tiket wajib diisi.'
      if (!this.form.category) return 'Kategori wajib dipilih.'
      if (!this.form.priority) return 'Prioritas wajib dipilih.'
      if (!this.form.description) return 'Deskripsi tiket wajib diisi.'
      return ''
    },
    async submit() {
      if (this.submitting || this.createdTicket) return
      this.apiError = ''
      const validationError = this.validate()
      if (validationError) {
        this.apiError = validationError
        return
      }

      const followUpDatetime = datetimeLocalToUnix(this.form.follow_up_local)
      if (this.form.follow_up_local && !followUpDatetime) {
        this.apiError = 'Jadwal tindak lanjut tidak valid.'
        return
      }

      const payload = {
        contact_id: String(this.form.contact_id || '').trim(),
        license_id: String(this.form.license_id || '').trim(),
        room_id: String(this.context && this.context.room_id ? this.context.room_id : '').trim(),
        contact_number: String(this.form.contact_number || '').trim(),
        contact_name: String(this.form.contact_name || '').trim(),
        title: this.form.title,
        description: this.form.description,
        category: this.form.category,
        priority: this.form.priority,
        assigned_agent_id: this.form.assigned_agent_id,
        follow_up_datetime: followUpDatetime
      }

      const queuedFiles = (this.attachmentFiles || []).slice()

      this.submitting = true
      let ticket = null
      try {
        const response = await this.$store.dispatch('ticket/create', payload)
        ticket = response && response.data ? response.data : null
        if (!ticket || !ticket.id) {
          throw new Error('Tiket berhasil dikirim tetapi respons tidak memuat ID tiket.')
        }
      } catch (error) {
        this.apiError = extractApiErrorMessage(error, 'Gagal membuat tiket')
        this.submitting = false
        return
      }

      // Tiket sudah dibuat. Jangan pernah membuat ulang tiket bila upload gagal.
      this.createdTicket = ticket
      this.submitting = false

      this.$emit('created', ticket, {
        attachmentsPending: queuedFiles.length > 0
      })

      if (!queuedFiles.length) return

      await this.runAttachmentUpload(ticket, queuedFiles)
    },
    async runAttachmentUpload(ticket, files) {
      const list = Array.from(files || []).filter(Boolean)
      if (!ticket || !ticket.id || !list.length) return
      if (this.attachmentUploading) return

      this.attachmentUploading = true
      this.attachmentProgress = 0
      this.attachmentUploadError = ''

      try {
        await this.$store.dispatch('ticket/uploadAttachments', {
          ticketId: ticket.id,
          files: list,
          onProgress: percent => {
            if (this.attachmentUploading) this.attachmentProgress = percent
          }
        })
        this.attachmentProgress = 100
        this.attachmentFiles = []
        this.$emit('completed', ticket, { attachmentsUploaded: list.length })
      } catch (error) {
        this.attachmentUploadError = extractApiErrorMessage(
          error,
          'Gambar gagal diunggah. Tiket sudah tersimpan.'
        )
      } finally {
        this.attachmentUploading = false
      }
    },
    handleAttachmentFilesChange(files) {
      this.attachmentFiles = Array.isArray(files) ? files.slice() : []
      if (!this.createdTicket) this.attachmentUploadError = ''
    },
    retryAttachmentUpload() {
      if (!this.createdTicket || this.attachmentUploading) return
      if (!this.attachmentFiles.length) return
      this.runAttachmentUpload(this.createdTicket, this.attachmentFiles.slice())
    },
    openCreatedTicketDetail() {
      if (!this.createdTicket) return
      if (this.attachmentUploading) return
      this.$emit('open-detail', this.createdTicket)
    },
    swalAboveModal(options) {
      const base = options || {}
      return this.$fire(
        Object.assign({}, base, {
          customClass: Object.assign({}, base.customClass || {}, {
            container: 'waajo-swal-above-ticket-modal'
          }),
          customContainerClass: 'waajo-swal-above-ticket-modal'
        })
      )
    },
    async close() {
      if (this.submitting || this.attachmentUploading) return
      if (this.createdTicket) {
        const hasQueued = this.attachmentFiles.length > 0
        const confirmation = await this.swalAboveModal({
          title: 'Tutup formulir?',
          text: hasQueued
            ? 'Tiket sudah dibuat, tetapi gambar belum terunggah. Antrean gambar akan dibuang.'
            : 'Tiket sudah dibuat.',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: hasQueued ? 'Ya, tutup' : 'Tutup',
          cancelButtonText: 'Kembali',
          confirmButtonColor: '#f39c12'
        })
        if (!confirmation || confirmation.value !== true) return
      }
      this.$emit('close')
    },
    handleBackdropClick() {
      this.close()
    }
  }
}
</script>

<style scoped>
.waajo-ticket-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 36px 18px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.56);
}
.waajo-ticket-modal {
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  overflow: hidden;
}
.waajo-ticket-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px 18px;
  border-bottom: 1px solid var(--waajo-border);
}
.waajo-ticket-modal-header h4 {
  margin: 0 0 5px;
  font-size: 20px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-modal-header h4 i {
  color: var(--waajo-brand);
  margin-right: 8px;
}
.waajo-ticket-modal-header p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
}
.waajo-ticket-modal-close {
  border: 0;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 20px;
  line-height: 1;
  padding: 2px;
}
.waajo-ticket-modal-body {
  padding: 20px 26px 24px;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
}
.waajo-ticket-context {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 10px;
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-context span {
  display: block;
  font-size: 12px;
  color: var(--waajo-brand-ink);
  text-transform: uppercase;
  letter-spacing: .04em;
  font-weight: 700;
}
.waajo-ticket-context strong {
  display: block;
  font-size: 14px;
  color: var(--waajo-text);
  margin-top: 3px;
  line-height: 1.4;
}
.waajo-ticket-context small {
  display: block;
  font-size: 12px;
  color: var(--waajo-text-muted);
  margin-top: 3px;
}
.waajo-ticket-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.waajo-ticket-form-group {
  margin-bottom: 16px;
}
.waajo-ticket-form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--waajo-text-body);
  margin-bottom: 7px;
  line-height: 1.4;
}
.waajo-ticket-form-group label em {
  color: #dc2626;
  font-style: normal;
}
.waajo-ticket-form-group input,
.waajo-ticket-form-group select,
.waajo-ticket-form-group textarea {
  width: 100%;
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--waajo-text);
  background: #fff;
  outline: none;
}
.waajo-ticket-form-group textarea {
  resize: vertical;
  min-height: 120px;
}
.waajo-ticket-form-group input:focus,
.waajo-ticket-form-group select:focus,
.waajo-ticket-form-group textarea:focus {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-ticket-form-group small {
  display: block;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
  margin-top: 6px;
}
.waajo-ticket-contact-box {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 12px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 10px;
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-field-help {
  color: var(--waajo-text-muted) !important;
}
.waajo-ticket-field-state.is-error {
  color: #b42318 !important;
}
.waajo-ticket-field-state.is-empty {
  color: var(--waajo-brand-ink) !important;
}
.waajo-ticket-retry-btn {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 12px;
  font-weight: 700;
  padding: 0 0 0 4px;
  text-decoration: underline;
}
.waajo-ticket-label-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}
.waajo-ticket-link-btn {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 12px;
  font-weight: 700;
  padding: 0;
}
.waajo-ticket-last-message {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: var(--waajo-brand-soft-2);
  border: 1px dashed var(--waajo-brand-border);
  color: var(--waajo-text-muted);
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-last-message i {
  color: var(--waajo-brand);
  margin-top: 2px;
}
.waajo-ticket-helper {
  color: var(--waajo-text-faint) !important;
}
.waajo-ticket-form-error {
  padding: 11px 13px;
  margin-top: 2px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-created-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 13px;
  margin-top: 2px;
  margin-bottom: 14px;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  background: #f0fdf4;
  color: #15803d;
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-created-notice.is-warning {
  border-color: var(--waajo-brand-border);
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink-strong);
}
.waajo-ticket-created-notice > i {
  margin-top: 2px;
}
.waajo-ticket-created-notice-body {
  min-width: 0;
}
.waajo-ticket-created-notice-body strong {
  display: block;
  font-size: 13px;
  font-weight: 700;
}
.waajo-ticket-created-notice-body p {
  margin: 4px 0 0;
}
.waajo-ticket-created-notice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 9px;
}
.waajo-ticket-created-notice-actions .waajo-ticket-btn {
  padding: 7px 13px;
  font-size: 12px;
}
.waajo-ticket-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 18px;
  margin-top: 8px;
  border-top: 1px solid var(--waajo-border);
}
.waajo-ticket-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: var(--waajo-text-body);
  border-radius: 9999px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.waajo-ticket-btn.is-primary {
  border-color: var(--waajo-brand);
  background: var(--waajo-brand);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 7px 16px rgba(243, 156, 18, 0.26);
}
.waajo-ticket-btn.is-primary:hover,
.waajo-ticket-btn.is-primary:focus {
  border-color: var(--waajo-brand-strong);
  background: var(--waajo-brand-strong);
}
.waajo-ticket-btn:disabled {
  opacity: .6;
}
@media (max-width: 640px) {
  .waajo-ticket-form-grid,
  .waajo-ticket-context {
    grid-template-columns: 1fr;
  }
  .waajo-ticket-modal-body {
    padding: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketCreateModal.vue